import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import {
  PlusIcon, ClipboardDocumentListIcon, ClockIcon, CheckCircleIcon,
  FunnelIcon, Squares2X2Icon, ListBulletIcon,
  CalendarIcon, XMarkIcon, ExclamationTriangleIcon,
  ChartBarIcon, BuildingOfficeIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { PageHeader } from '../../components/layout';
import {
  Card, CardBody, Button, SearchInput, Select, Avatar, AvatarGroup,
  StatusBadge, PriorityBadge, Pagination, EmptyState, PageLoader,
  Tabs, TabList, Tab, Badge, ProgressBar
} from '../../components/ui';
import { taskService, projectService } from '../../services';
import { useAuthStore } from '../../stores';

export default function TaskList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user: currentUser } = useAuthStore();
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [viewMode, setViewMode] = useState('list'); // 'grid' or 'list'
  const [showFilters, setShowFilters] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    priority: '',
    project: '',
    sort: '-createdAt'
  });
  const [stats, setStats] = useState({
    total: 0,
    todo: 0,
    inProgress: 0,
    completed: 0,
    overdue: 0
  });

  useEffect(() => {
    fetchTasks();
    fetchProjects();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, filters, activeTab]);

  useEffect(() => {
    calculateStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks]);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        sort: filters.sort,
        ...filters
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      if (activeTab === 'overdue') {
        params.overdue = true;
      } else if (activeTab === 'today') {
        params.dueToday = true;
      }

      const response = await taskService.getMyTasks(params);
      setTasks(response.data);
      setPagination(response.pagination);
    } catch (error) {
      toast.error(t('errors.serverError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await projectService.getProjects({ limit: 100 });
      setProjects(response.data || []);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };

  const calculateStats = () => {
    if (tasks.length === 0) {
      setStats({ total: 0, todo: 0, inProgress: 0, completed: 0, overdue: 0 });
      return;
    }

    const todo = tasks.filter(t => t.status === 'todo').length;
    const inProgress = tasks.filter(t => t.status === 'in_progress').length;
    const completed = tasks.filter(t => t.status === 'completed').length;
    const overdue = tasks.filter(t => {
      if (!t.dueDate || t.status === 'completed') return false;
      return dayjs(t.dueDate).isBefore(dayjs(), 'day');
    }).length;

    setStats({
      total: pagination.total,
      todo,
      inProgress,
      completed,
      overdue
    });
  };

  const handleSearch = useCallback((value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: '',
      priority: '',
      project: '',
      sort: '-createdAt'
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const isOverdue = (task) => {
    return task.dueDate && dayjs(task.dueDate).isBefore(dayjs(), 'day') && task.status !== 'completed';
  };

  const statusOptions = [
    { value: '', label: t('common.all') },
    { value: 'todo', label: t('tasks.statuses.todo') },
    { value: 'in_progress', label: t('tasks.statuses.inProgress') },
    { value: 'review', label: t('tasks.statuses.review') },
    { value: 'completed', label: t('tasks.statuses.completed') },
    { value: 'cancelled', label: t('tasks.statuses.cancelled') }
  ];

  const priorityOptions = [
    { value: '', label: t('common.all') },
    { value: 'low', label: t('projects.priorities.low') },
    { value: 'medium', label: t('projects.priorities.medium') },
    { value: 'high', label: t('projects.priorities.high') },
    { value: 'urgent', label: t('projects.priorities.urgent') }
  ];

  const sortOptions = [
    { value: '-createdAt', label: t('common.newest') || 'Newest First' },
    { value: 'createdAt', label: t('common.oldest') || 'Oldest First' },
    { value: 'title', label: t('common.name') + ' (A-Z)' },
    { value: '-title', label: t('common.name') + ' (Z-A)' },
    { value: '-dueDate', label: t('tasks.dueDate') + ' (Latest)' },
    { value: 'dueDate', label: t('tasks.dueDate') + ' (Earliest)' },
    { value: '-priority', label: t('common.priority') + ' (High)' },
    { value: 'priority', label: t('common.priority') + ' (Low)' }
  ];

  const projectOptions = [
    { value: '', label: t('common.all') },
    ...projects.map(project => ({
      value: project._id,
      label: project.name
    }))
  ];

  const hasActiveFilters = filters.status || filters.priority || filters.project;

  // Check if user can create tasks (has canManageTasks permission in at least one project)
  const canCreateTask = () => {
    if (!currentUser) return false;

    // Check if user has canManageTasks permission in any project
    return projects.some(project => {
      // Owner can always create tasks
      if (project.owner?._id?.toString() === currentUser._id?.toString()) {
        return true;
      }

      // Check member permissions
      const member = project.members?.find(m =>
        m.user?._id?.toString() === currentUser._id?.toString() ||
        m.user?.toString() === currentUser._id?.toString()
      );

      return member?.permissions?.canManageTasks === true;
    });
  };

  const statsCards = [
    {
      label: t('common.all'),
      value: stats.total,
      icon: ClipboardDocumentListIcon,
      color: 'text-blue-500',
      bgColor: 'bg-blue-50'
    },
    {
      label: t('tasks.statuses.todo'),
      value: stats.todo,
      icon: ClipboardDocumentListIcon,
      color: 'text-gray-500',
      bgColor: 'bg-gray-50'
    },
    {
      label: t('tasks.statuses.inProgress'),
      value: stats.inProgress,
      icon: ChartBarIcon,
      color: 'text-orange-500',
      bgColor: 'bg-orange-50'
    },
    {
      label: t('tasks.statuses.completed'),
      value: stats.completed,
      icon: CheckCircleIcon,
      color: 'text-green-500',
      bgColor: 'bg-green-50'
    },
    {
      label: t('tasks.overdue'),
      value: stats.overdue,
      icon: ExclamationTriangleIcon,
      color: 'text-red-500',
      bgColor: 'bg-red-50'
    }
  ];

  return (
    <>
      <Helmet>
        <title>{t('tasks.title')} - ProCTM</title>
      </Helmet>

      <PageHeader
        title={t('tasks.myTasks')}
        actions={
          canCreateTask() && (
            <Button icon={PlusIcon} onClick={() => navigate('/tasks/new')}>
              {t('tasks.newTask')}
            </Button>
          )
        }
      />

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        {statsCards.map((stat, index) => (
          <Card key={index} className="hover:shadow-md transition-shadow">
            <CardBody>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 mb-1">{stat.label}</p>
                  <p className="text-xl font-bold text-neutral-dark">{stat.value}</p>
                </div>
                <div className={`${stat.bgColor} p-2.5 rounded-lg`}>
                  <stat.icon className={`w-5 h-5 ${stat.color}`} />
                </div>
              </div>
            </CardBody>
          </Card>
        ))}
      </div>

      {/* Filters and Search */}
      <Card className="mb-6">
        <CardBody>
          <div className="flex flex-col gap-4">
            {/* Main Search and Controls */}
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <div className="flex-1 w-full">
                <SearchInput
                  value={filters.search}
                  onChange={handleSearch}
                  placeholder={t('common.search') + ' ' + t('tasks.title').toLowerCase() + '...'}
                  className="w-full"
                />
              </div>
              <div className="flex items-center gap-2">
                {/* View Mode Toggle */}
                <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-lg">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}
                    title="Grid View"
                  >
                    <Squares2X2Icon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
                    title="List View"
                  >
                    <ListBulletIcon className="w-4 h-4" />
                  </button>
                </div>
                {/* Filter Toggle */}
                <Button
                  variant="outline"
                  size="sm"
                  icon={FunnelIcon}
                  onClick={() => setShowFilters(!showFilters)}
                  className={hasActiveFilters ? 'border-primary text-primary' : ''}
                >
                  {t('common.filter')}
                  {hasActiveFilters && (
                    <span className="ml-1 bg-primary text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                      {[filters.status, filters.priority, filters.project].filter(Boolean).length}
                    </span>
                  )}
                </Button>
              </div>
            </div>

            {/* Tabs */}
            <Tabs>
              <TabList>
                <Tab active={activeTab === 'all'} onClick={() => handleTabChange('all')}>
                  {t('tasks.allTasks')} ({stats.total})
                </Tab>
                <Tab active={activeTab === 'today'} onClick={() => handleTabChange('today')}>
                  {t('tasks.dueToday')}
                </Tab>
                <Tab active={activeTab === 'overdue'} onClick={() => handleTabChange('overdue')}>
                  {t('tasks.overdue')} ({stats.overdue})
                </Tab>
              </TabList>
            </Tabs>

            {/* Advanced Filters */}
            {showFilters && (
              <div className="pt-4 border-t border-gray-200">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Select
                    label={t('common.status')}
                    options={statusOptions}
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  />
                  <Select
                    label={t('common.priority')}
                    options={priorityOptions}
                    value={filters.priority}
                    onChange={(e) => handleFilterChange('priority', e.target.value)}
                  />
                  <Select
                    label={t('projects.title')}
                    options={projectOptions}
                    value={filters.project}
                    onChange={(e) => handleFilterChange('project', e.target.value)}
                  />
                  <Select
                    label={t('common.sort') || 'Sort By'}
                    options={sortOptions}
                    value={filters.sort}
                    onChange={(e) => handleFilterChange('sort', e.target.value)}
                  />
                </div>
                {hasActiveFilters && (
                  <div className="mt-4 flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearFilters}
                      icon={XMarkIcon}
                    >
                      {t('common.clearFilters') || 'Clear Filters'}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Tasks List */}
      {loading ? (
        <PageLoader />
      ) : tasks.length === 0 ? (
        <Card>
          <CardBody>
            <EmptyState
              icon={ClipboardDocumentListIcon}
              title={t('tasks.noTasks')}
              description={t('tasks.noTasksDesc')}
              action={() => navigate('/tasks/new')}
              actionLabel={t('tasks.newTask')}
            />
          </CardBody>
        </Card>
      ) : (
        <>
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tasks.map((task) => {
                const taskOverdue = isOverdue(task);
                return (
                  <Card
                    key={task._id}
                    className="hover:shadow-lg transition-all cursor-pointer group"
                    onClick={() => navigate(`/tasks/${task._id}`)}
                  >
                    <CardBody>
                      {/* Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1 min-w-0">
                          <h3 className={`font-semibold text-lg line-clamp-2 mb-1 group-hover:text-primary transition-colors ${task.status === 'completed' ? 'text-gray-400 line-through' : 'text-neutral-dark'
                            }`}>
                            {task.title}
                          </h3>
                          {task.project && (
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <BuildingOfficeIcon className="w-4 h-4" />
                              <Link
                                to={`/projects/${task.project._id}`}
                                onClick={(e) => e.stopPropagation()}
                                className="hover:text-primary hover:underline"
                              >
                                {task.project.name}
                              </Link>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <StatusBadge status={task.status} />
                          <PriorityBadge priority={task.priority} />
                        </div>
                      </div>

                      {/* Description */}
                      {task.description && (
                        <p className="text-sm text-gray-600 line-clamp-2 mb-4">
                          {task.description}
                        </p>
                      )}

                      {/* Progress */}
                      {task.progress !== undefined && (
                        <div className="mb-4">
                          <div className="flex items-center justify-between text-sm mb-2">
                            <span className="text-gray-500">{t('common.progress')}</span>
                            <span className="font-semibold text-primary">{task.progress}%</span>
                          </div>
                          <ProgressBar value={task.progress} color="primary" size="md" />
                        </div>
                      )}

                      {/* Info */}
                      <div className="space-y-2 mb-4 text-sm">
                        {task.dueDate && (
                          <div className={`flex items-center gap-2 ${taskOverdue ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                            <ClockIcon className="w-4 h-4" />
                            <span>
                              {t('tasks.dueDate')}: {dayjs(task.dueDate).format('MMM D, YYYY')}
                              {taskOverdue && ' ⚠️'}
                            </span>
                          </div>
                        )}
                        {task.startDate && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <CalendarIcon className="w-4 h-4" />
                            <span>
                              {t('projects.startDate')}: {dayjs(task.startDate).format('MMM D, YYYY')}
                            </span>
                          </div>
                        )}
                        {task.estimatedHours > 0 && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <ClockIcon className="w-4 h-4" />
                            <span>{t('tasks.estimatedHours')}: {task.estimatedHours}h</span>
                          </div>
                        )}
                      </div>

                      {/* Tags */}
                      {task.tags && task.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-4">
                          {task.tags.slice(0, 3).map((tag, index) => (
                            <Badge key={index} variant="neutral" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                          {task.tags.length > 3 && (
                            <Badge variant="neutral" className="text-xs">
                              +{task.tags.length - 3}
                            </Badge>
                          )}
                        </div>
                      )}

                      {/* Footer */}
                      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                        {task.assignees && task.assignees.length > 0 ? (
                          <AvatarGroup
                            users={task.assignees}
                            max={4}
                            size="xs"
                          />
                        ) : (
                          <span className="text-xs text-gray-400">{t('tasks.noAssignees') || 'Unassigned'}</span>
                        )}
                        {task.createdBy && (
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span>{t('common.createdBy') || 'Created by'}</span>
                            <Avatar
                              src={process.env.REACT_APP_SOCKET_URL + task.createdBy.avatar}
                              name={`${task.createdBy.firstName} ${task.createdBy.lastName}`}
                              size="xs"
                            />
                          </div>
                        )}
                      </div>
                    </CardBody>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card>
              <CardBody>
                <div className="space-y-3">
                  {tasks.map((task) => {
                    const taskOverdue = isOverdue(task);
                    return (
                      <div
                        key={task._id}
                        className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:border-primary hover:shadow-md transition-all cursor-pointer"
                        onClick={() => navigate(`/tasks/${task._id}`)}
                      >
                        {/* Status Indicator */}
                        <div className={`w-1 h-16 rounded-full ${task.status === 'completed' ? 'bg-green-500' :
                            task.status === 'in_progress' ? 'bg-blue-500' :
                              task.status === 'review' ? 'bg-yellow-500' :
                                task.status === 'cancelled' ? 'bg-red-500' :
                                  taskOverdue ? 'bg-red-500' :
                                    'bg-gray-400'
                          }`} />

                        {/* Task Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className={`font-semibold text-lg ${task.status === 'completed' ? 'text-gray-400 line-through' : 'text-neutral-dark'
                              }`}>
                              {task.title}
                            </h3>
                            <StatusBadge status={task.status} />
                            <PriorityBadge priority={task.priority} />
                            {taskOverdue && (
                              <Badge variant="danger" className="text-xs">
                                {t('tasks.overdue')}
                              </Badge>
                            )}
                          </div>
                          {task.description && (
                            <p className="text-sm text-gray-600 line-clamp-1 mb-2">
                              {task.description}
                            </p>
                          )}
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            {task.project && (
                              <div className="flex items-center gap-1">
                                <BuildingOfficeIcon className="w-4 h-4" />
                                <Link
                                  to={`/projects/${task.project._id}`}
                                  onClick={(e) => e.stopPropagation()}
                                  className="hover:text-primary hover:underline"
                                >
                                  {task.project.name}
                                </Link>
                              </div>
                            )}
                            {task.dueDate && (
                              <div className={`flex items-center gap-1 ${taskOverdue ? 'text-red-600 font-medium' : ''}`}>
                                <ClockIcon className="w-4 h-4" />
                                <span>
                                  {dayjs(task.dueDate).format('MMM D, YYYY')}
                                  {taskOverdue && ' (Overdue)'}
                                </span>
                              </div>
                            )}
                            {task.estimatedHours > 0 && (
                              <div className="flex items-center gap-1">
                                <ClockIcon className="w-4 h-4" />
                                <span>{task.estimatedHours}h {t('tasks.estimated') || 'estimated'}</span>
                              </div>
                            )}
                            {task.actualHours > 0 && (
                              <div className="flex items-center gap-1">
                                <ClockIcon className="w-4 h-4" />
                                <span>{task.actualHours}h {t('tasks.actual') || 'actual'}</span>
                              </div>
                            )}
                          </div>
                          {task.tags && task.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {task.tags.slice(0, 5).map((tag, index) => (
                                <Badge key={index} variant="neutral" className="text-xs">
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Assignees and Actions */}
                        <div className="flex items-center gap-4">
                          {task.assignees && task.assignees.length > 0 ? (
                            <AvatarGroup
                              users={task.assignees}
                              max={4}
                              size="xs"
                            />
                          ) : (
                            <span className="text-xs text-gray-400">{t('tasks.noAssignees') || 'Unassigned'}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardBody>
            </Card>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="mt-6">
              <Pagination
                page={pagination.page}
                limit={pagination.limit}
                total={pagination.total}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </>
      )}
    </>
  );
}